<script setup lang="ts">
/**
 * The shell. There is no router: one read of the URL at start-up (and on
 * `popstate`) decides which of a handful of screens renders, and the session
 * decides the rest.
 *
 * Precedence, top to bottom:
 *   not-found  — a URL none of the routes below recognise
 *   admin      — `/admin-panel[/section]`, once the session has resolved and is signed in
 *   link       — `/verify-email?token=` or `/reset-password?token=`, from an email;
 *                `/account/confirm?action=restart&token=` waits for a signed-in, verified captain
 *   restoring  — the refresh cookie is being redeemed
 *   auth       — nobody signed in
 *   unverified — signed in, email not yet confirmed
 *   home       — the anchorage: a live harbour with your ship in it, and the
 *                shipyard, record and shop reached by going to them
 *   sea        — the battle, entered from the harbour; joins the server arena
 *
 * The shipyard and the record are no longer phases. They are panels inside
 * the anchorage, because a refit you can see on the water is the point of
 * having a harbour at all — leaving the scene to change a gun would be the
 * flat screen this replaced. `AnchorageScreen` falls back to the old flat
 * `HomeScreen` when WebGL is unavailable, and that fallback still reaches
 * both panels.
 *
 * Screen changes are animated: the screens sit in one keyed wrapper inside a
 * `<Transition mode="out-in">` (see `screenKey`), and a sortie or the return
 * from one is hidden by the canvas tarp (`ui/motion/Tarp.vue`).
 *
 * Dev only: `/dev/sea` renders the sea scene directly, without a session, so
 * the renderer can be worked on with no backend running — the scene then runs
 * a local arena with bots. `/dev/harbour` does the same for the anchorage.
 * Both branches are behind `import.meta.env.DEV`, so production bundles do
 * not contain them.
 */
import { computed, defineAsyncComponent, defineComponent, h, onBeforeUnmount, onMounted, ref } from 'vue';

const AdminScreen = defineAsyncComponent(() => import('./admin/AdminScreen.vue'));
import { useI18n } from './i18n';
import ConfirmSheet from './ui/ConfirmSheet.vue';
import ConfirmSignOut from './ui/ConfirmSignOut.vue';
import CookieConsent from './ui/consent/CookieConsent.vue';
import SettingsSheet from './ui/SettingsSheet.vue';
import JoinScreen from './ui/JoinScreen.vue';
// Plain import, not async: the landing page itself is light — its hero pulls
// Three.js, and that is lazy inside the hero component, not here.
import LandingScreen from './landing/LandingScreen.vue';
import LoadingScreen from './ui/LoadingScreen.vue';
import SlipHost from './ui/motion/SlipHost.vue';
import Tarp from './ui/motion/Tarp.vue';
import { enter, leave } from './ui/motion/gsap';
import { tarp } from './ui/motion/tarp';
import NotFoundScreen from './ui/NotFoundScreen.vue';
import ResetPasswordScreen from './ui/ResetPasswordScreen.vue';
import RestartConfirmScreen from './ui/RestartConfirmScreen.vue';
import VerifyEmailGate from './ui/VerifyEmailGate.vue';
import VerifyEmailScreen from './ui/VerifyEmailScreen.vue';
import { readAdminRoute, type AdminRoute } from './state/admin-route';
import { readTopupReturn, type TopupReturn } from './state/topup-return';
import { clearDeepLink, readDeepLink, type DeepLink } from './state/deep-link';
import { readIsNotFound } from './state/not-found';
import { useSession } from './state/session';

const session = useSession();
const { t } = useI18n();

// Three.js only ships to people who get past the gate; auth and admin stay
// light. The harbour and the battle are separate chunks: a signed-in player
// always pays for the harbour, and only pays for the battle when they sail.
// The wait for each chunk shows its own painting: `loadingComponent` takes a
// component, not props, so each is a one-line wrapper round the screen.
const SeaLoading = defineComponent({ setup: () => () => h(LoadingScreen, { art: 'battle', subtitle: t('loading.sea') }) });
// `instant`: this painting follows the session restore's and precedes the
// harbour's own, all three the same picture — a fade-in on each would flash.
const HarbourLoading = defineComponent({
  setup: () => () => h(LoadingScreen, { art: 'harbour', subtitle: t('loading.harbour'), instant: true }),
});
const SeaScreen = defineAsyncComponent({
  loader: () => import('./ui/SeaScreen.vue'),
  loadingComponent: SeaLoading,
});
const AnchorageScreen = defineAsyncComponent({
  loader: () => import('./ui/AnchorageScreen.vue'),
  loadingComponent: HarbourLoading,
});

const deepLink = ref<DeepLink | null>(readDeepLink());
const adminRoute = ref<AdminRoute | null>(readAdminRoute());
const notFound = ref(readIsNotFound());
const atSea = ref(false);
/** The sortie the harbour asked for: a battle, or the training run. */
const seaMode = ref<'battle' | 'training'>('battle');
async function putToSea(mode: 'battle' | 'training'): Promise<void> {
  // The tarp covers the swap for every way of sailing, the shipyard's button
  // included; the sea lifts it when it has arrived (`onScreenEnter`).
  await tarp.close(t('tarp.sortie'));
  seaMode.value = mode;
  atSea.value = true;
}

/** Back to the harbour: tarp down, then the battle goes; the harbour lifts it when ready. */
async function leaveSea(): Promise<void> {
  await tarp.close(t('tarp.harbour'));
  atSea.value = false;
}
/** Back from the payment gateway: the harbour opens on the chandler's. */
const topupReturn = ref<TopupReturn | null>(readTopupReturn());

/**
 * The `auth` phase's own two faces: the landing a stranger opens on, and the
 * enlistment form it opens into. Not a route — signing out always returns
 * here, because the ref's default is `'landing'`.
 */
const authView = ref<'landing' | 'form'>('landing');
const authFormMode = ref<'login' | 'register'>('login');
function openAuthForm(initialMode: 'login' | 'register'): void {
  authFormMode.value = initialMode;
  authView.value = 'form';
}

const devPath = import.meta.env.DEV && typeof window !== 'undefined' ? window.location.pathname : '';
const DEV_SEA = devPath === '/dev/sea';
const DEV_HARBOUR = devPath === '/dev/harbour';

function reread(): void {
  adminRoute.value = readAdminRoute();
  notFound.value = readIsNotFound();
}

type Phase = 'not-found' | 'admin' | 'link' | 'restoring' | 'auth' | 'unverified' | 'home' | 'sea';

const phase = computed<Phase>(() => {
  if (DEV_SEA) return 'sea';
  if (DEV_HARBOUR) return 'home';
  if (notFound.value) return 'not-found';
  if (adminRoute.value && !session.restoring.value && session.signedIn.value) return 'admin';
  // The restart link acts on a signed-in account: it waits behind the sign-in and the verification gate.
  if (deepLink.value && deepLink.value.kind !== 'restart-confirm') return 'link';
  if (session.restoring.value) return 'restoring';
  if (!session.signedIn.value) return 'auth';
  if (!session.player.value?.emailVerified) return 'unverified';
  if (deepLink.value) return 'link';
  if (atSea.value) return 'sea';
  return 'home';
});

/**
 * Which screen is on show, as a key: when it changes the old one leaves and
 * the new one arrives. The session restore and the harbour share a key — the
 * same painting stands there while the session resolves, the chunk loads and
 * the scene builds, and sliding between them would be motion for its own sake.
 */
const screenKey = computed(() => {
  if (phase.value === 'restoring' || phase.value === 'home') return 'harbour';
  if (phase.value === 'auth') return authView.value === 'landing' ? 'landing' : 'join';
  return phase.value;
});

// Screen change (life-layer.md §3, P0): the old one slides off a little to the
// left, the new one in from the right. Both are skipped while the tarp is
// down — it is already hiding the swap, and 180 ms under it is only delay.
const SCREEN_LEAVE = { opacity: 0, xPercent: -4, duration: 0.18, ease: 'power2.in' };
const SCREEN_ENTER = { opacity: 0, xPercent: 4, duration: 0.26, ease: 'power3.out' };

function onScreenLeave(el: Element, done: () => void): void {
  // Under the tarp there is nothing to animate, but `done` must not run inside
  // the hook itself: with `mode="out-in"` Vue then re-renders before the old
  // screen has left the DOM and patches against a detached node (the battle
  // came up black). One frame later is enough.
  // `leave` also finishes synchronously under reduced motion, so both paths defer.
  const later = () => requestAnimationFrame(() => done());
  if (tarp.isClosed()) later();
  else leave(el, SCREEN_LEAVE, later);
}

function onScreenEnter(el: Element, done: () => void): void {
  if (tarp.isClosed()) {
    done();
    // The battle lifts the tarp when it emits `ready` (first frame drawn, or
    // the scene failed); the harbour does it itself once its scene is built.
  } else {
    enter(el, SCREEN_ENTER, done);
  }
}

function leaveDeepLink(): void {
  clearDeepLink();
  deepLink.value = null;
}

onMounted(() => {
  window.addEventListener('popstate', reread);
  if (!DEV_SEA && !DEV_HARBOUR) void session.restore();
});

onBeforeUnmount(() => {
  window.removeEventListener('popstate', reread);
});
</script>

<template>
  <Transition :css="false" mode="out-in" @leave="onScreenLeave" @enter="onScreenEnter">
    <div :key="screenKey" class="h-full">
      <NotFoundScreen v-if="phase === 'not-found'" />

      <AdminScreen v-else-if="phase === 'admin'" :route="adminRoute!" />

      <VerifyEmailScreen
        v-else-if="phase === 'link' && deepLink?.kind === 'verify-email'"
        :token="deepLink.token"
        @done="leaveDeepLink"
      />
      <RestartConfirmScreen
        v-else-if="phase === 'link' && deepLink?.kind === 'restart-confirm'"
        :token="deepLink.token"
        @done="leaveDeepLink"
      />
      <ResetPasswordScreen v-else-if="phase === 'link'" :token="deepLink!.token" @done="leaveDeepLink" />

      <LoadingScreen v-else-if="phase === 'restoring'" art="harbour" :subtitle="t('loading.papers')" :tips="false" />

      <LandingScreen
        v-else-if="phase === 'auth' && authView === 'landing'"
        @play="openAuthForm('register')"
        @sign-in="openAuthForm('login')"
      />
      <JoinScreen
        v-else-if="phase === 'auth'"
        :initial-mode="authFormMode"
        @back="authView = 'landing'"
      />

      <VerifyEmailGate v-else-if="phase === 'unverified'" />

      <SeaScreen
        v-else-if="phase === 'sea'"
        :key="seaMode"
        :online="session.signedIn.value"
        :training="seaMode === 'training'"
        @back="leaveSea()"
        @skip="seaMode = 'battle'"
        @ready="tarp.open()"
      />

      <!-- v-if, not v-show: the harbour's WebGL context must be gone before the
           battle builds its own. -->
      <AnchorageScreen v-else :topup-return="topupReturn" @sail="putToSea('battle')" @train="putToSea('training')" @topup-seen="topupReturn = null" />
    </div>
  </Transition>

  <!-- Unconditional: each draws nothing until asked. -->
  <ConfirmSignOut />
  <ConfirmSheet />
  <SettingsSheet />
  <CookieConsent :banner="phase !== 'sea'" />
  <SlipHost />
  <Tarp />
</template>
